import { Button, Card, Input, Loader, Select, Stack, Text, Textarea, Title } from "@mantine/core";

import classes from "@/pages/saveBookingPage.module.css";
import { IconArrowBack } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useState } from "react";
import { CreateBooking } from "@/api/booking";
import { useMediaQuery } from "@mantine/hooks";
import { PhoneInput } from "../PhoneInput/PhoneInput";
import { isValidEmail } from "@/utils/validations";

interface SaveBookingContactInfoProps {
  loading: boolean;
  prevStep: () => void;
  isLocationAvailable: boolean;
  createBooking: CreateBooking;
  setCreateBooking: React.Dispatch<React.SetStateAction<CreateBooking>>;
  onClick: () => void;
  defaultCountryCode?: string | undefined;
}

type SessionMode = 'onSite' | 'virtual';
type LocationOption = { value: SessionMode; label: string };
type FormErrors = {
  [key: string]: string | undefined;
};

export const SaveBookingContactInfo = ({
  loading, prevStep, isLocationAvailable, createBooking, setCreateBooking, onClick, defaultCountryCode
}: SaveBookingContactInfoProps) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const locationSelectOptions = useMemo<LocationOption[]>(
    () => [
      { value: 'onSite', label: t('save_booking_page.fill_data.on_site') },
      { value: 'virtual', label: t('save_booking_page.fill_data.virtual') },
    ],
    [t]
  );
  const sessionType = useMemo<SessionMode>(
    () => (createBooking.onSite ? 'onSite' : 'virtual'),
    [createBooking.onSite]
  );

  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isPhoneValid, setIsPhoneValid] = useState(true);

  const checkBookingForm = useCallback(() => {
    let hasError = false;
    setFormErrors({});
    let localFormErrors = {};

    if (!createBooking?.name) {
      localFormErrors = {
        ...localFormErrors,
        name: t('booking_modal.name_error'),
      };
      hasError = true;
    }

    if (!createBooking?.email) {
      localFormErrors = {
        ...localFormErrors,
        email: t('booking_modal.email_error'),
      };
      hasError = true;
    } else if (!isValidEmail(createBooking?.email)) {
      localFormErrors = {
        ...localFormErrors,
        email: t('booking_modal.email_invalid'),
      };
      hasError = true;
    }

    if (!isPhoneValid) {
      localFormErrors = {
        ...localFormErrors,
        phone: t('booking_modal.phone_error'),
      };
      hasError = true;
    }

    setFormErrors({ ...localFormErrors });

    return hasError;
  }, [isPhoneValid, createBooking]);

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder className={classes.cardTwo}>
      {loading && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translateY(-50%) translateX(-50%)',
          }}
        >
          <Loader></Loader>
        </div>
      )}
      <IconArrowBack
        size={32}
        style={{ position: 'absolute', bottom: '1.25rem', left: '1.25rem' }}
        className={classes.arrowForward}
        onClick={() => {
          if (!loading) prevStep();
        }}
      />
      <Title order={3} style={{ textAlign: 'center', marginBottom: '25px' }}>
        {t('save_booking_page.fill_data.title')}
      </Title>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <div className={classes.formContainer}>
          <div>
            {isLocationAvailable && (
                <Select
                  data={locationSelectOptions}
                  value={sessionType}
                  label={t('save_booking_page.fill_data.session_type_label')}
                  onChange={(e) =>
                    setCreateBooking({ ...createBooking, onSite: e === 'onSite' })
                  }
                />
              )}

            <Input.Wrapper
              label={t('booking_modal.name')}
              error={formErrors?.name ? formErrors.name : ''}
            >
              <Input
                value={createBooking.name}
                placeholder={t('booking_modal.name_placeholder')}
                onChange={(e) =>
                  setCreateBooking({ ...createBooking, name: e.target.value })
                }
                disabled={loading}
              />
            </Input.Wrapper>
            <Input.Wrapper
              label={t('booking_modal.email')}
              error={formErrors?.email ? formErrors.email : ''}
              style={{ width: `${isMobile ? '100%' : '60%'}` }}
            >
              <Input
                value={createBooking.email}
                placeholder="example@email.com"
                onChange={(e) =>
                  setCreateBooking({ ...createBooking, email: e.target.value })
                }
                disabled={loading}
              />
            </Input.Wrapper>
            <PhoneInput
              label={t('booking_modal.phone')}
              disabled={loading}
              defaultValue={createBooking.phone}
              defaultCallingCode={defaultCountryCode || '+52'}
              // defaultError={formErrors?.phone || ""}
              onChange={(value, code, isValid) => {
                if (value) setIsPhoneValid(isValid);
                setCreateBooking({ ...createBooking, phone: value });
              }}
            />
          </div>

          <Stack align="flex-end" gap="xs">
            <Textarea
              label={`${t('save_booking_page.fill_data.comments')}:`}
              autosize
              maxLength={250}
              minRows={5}
              maxRows={10}
              w="100%"
              value={createBooking.comments}
              onChange={(e) =>
                setCreateBooking({ ...createBooking, comments: e.target.value })
              }
              disabled={loading}
            />
            <Text
              size="xs"
              c={
                createBooking.comments.length < 250 ? 'dimmed' : 'var(--mantine-background)'
              }
              className={createBooking.comments.length >= 250 ? 'shake' : ''}
            >
              {createBooking.comments.length}/250{' '}
              {t('save_booking_page.fill_data.characters')}
            </Text>
          </Stack>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-evenly',
            marginTop: '15px',
          }}
        >
          <Button
            size="md"
            onClick={() => {
              if ( checkBookingForm() ) return;
              onClick();
            }}
            disabled={loading || createBooking.comments.length > 250}
          >
            Guardar
          </Button>
        </div>
      </div>
    </Card>
  )
}